import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface CustomerDetailsDialogProps {
  customerId: string | null;
  customerName: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CustomerDetailsDialog({
  customerId,
  customerName,
  isOpen,
  onOpenChange,
}: CustomerDetailsDialogProps) {
  const { data: transactions, isLoading: isLoadingTransactions } = useQuery({
    queryKey: ["customerTransactions", customerId],
    queryFn: async () => {
      if (!customerId) return [];
      const { data, error } = await supabase
        .from("transactions")
        .select("*")
        .eq("customer_id", customerId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!customerId && isOpen,
  });

  const { data: quotations, isLoading: isLoadingQuotations } = useQuery({
    queryKey: ["customerQuotations", customerId],
    queryFn: async () => {
      if (!customerId) return [];
      const { data, error } = await supabase
        .from("quotations")
        .select("*")
        .eq("customer_id", customerId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!customerId && isOpen,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "pago":
        return "bg-green-500/10 text-green-500";
      case "pendente":
        return "bg-yellow-500/10 text-yellow-500";
      case "atrasado":
        return "bg-red-500/10 text-red-500";
      default:
        return "bg-gray-500/10 text-gray-500";
    }
  };

  const getQuotationStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "accepted":
        return "bg-green-500/10 text-green-500";
      case "pending":
        return "bg-yellow-500/10 text-yellow-500";
      default:
        return "bg-gray-500/10 text-gray-500";
    }
  };

  const getQuotationStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case "accepted":
        return "Aceito";
      case "pending":
        return "Pendente";
      default:
        return status;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-[#1A1F2C] text-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white">
            Detalhes de {customerName}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="transactions" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="transactions">Transações</TabsTrigger>
            <TabsTrigger value="quotations">Orçamentos</TabsTrigger>
          </TabsList>

          <TabsContent value="transactions">
            {isLoadingTransactions ? (
              <div className="py-4">Carregando transações...</div>
            ) : !transactions?.length ? (
              <div className="py-4">Nenhuma transação encontrada.</div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto">
                <table className="w-full">
                  <thead className="border-b border-[#404040]">
                    <tr>
                      <th className="py-2 text-left text-[#8A9099]">Data</th>
                      <th className="py-2 text-left text-[#8A9099]">Tipo</th>
                      <th className="py-2 text-left text-[#8A9099]">Descrição</th>
                      <th className="py-2 text-left text-[#8A9099]">Valor</th>
                      <th className="py-2 text-left text-[#8A9099]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((transaction) => (
                      <tr
                        key={transaction.id}
                        className="border-b border-[#404040] hover:bg-[#222831]"
                      >
                        <td className="py-2">
                          {format(new Date(transaction.created_at), "dd/MM/yyyy")}
                        </td>
                        <td className="py-2">{transaction.type}</td>
                        <td className="py-2">{transaction.description}</td>
                        <td className="py-2">
                          {formatCurrency(transaction.amount)}
                        </td>
                        <td className="py-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(
                              transaction.payment_status
                            )}`}
                          >
                            {transaction.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="quotations">
            {isLoadingQuotations ? (
              <div className="py-4">Carregando orçamentos...</div>
            ) : !quotations?.length ? (
              <div className="py-4">Nenhum orçamento encontrado.</div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto">
                <table className="w-full">
                  <thead className="border-b border-[#404040]">
                    <tr>
                      <th className="py-2 text-left text-[#8A9099]">Código</th>
                      <th className="py-2 text-left text-[#8A9099]">Data</th>
                      <th className="py-2 text-left text-[#8A9099]">Descrição</th>
                      <th className="py-2 text-left text-[#8A9099]">Valor</th>
                      <th className="py-2 text-left text-[#8A9099]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotations.map((quotation) => (
                      <tr
                        key={quotation.id}
                        className="border-b border-[#404040] hover:bg-[#222831]"
                      >
                        <td className="py-2">{quotation.code}</td>
                        <td className="py-2">
                          {format(new Date(quotation.created_at), "dd/MM/yyyy")}
                        </td>
                        <td className="py-2">{quotation.description}</td>
                        <td className="py-2">
                          {formatCurrency(quotation.total_amount)}
                        </td>
                        <td className="py-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getQuotationStatusColor(
                              quotation.status
                            )}`}
                          >
                            {getQuotationStatusLabel(quotation.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}